import { useSetAtom } from 'jotai';
import { Card } from 'primereact/card';
import { Skeleton } from 'primereact/skeleton';
import { useCallback, useMemo } from 'react';
import { selectWidgetAIAtom } from '~/atoms/dashboardAtom';
import { IndicatorDot } from '../../misc/widget/indicatorDot';
import { NoDataAvailableTemplate } from '../../common/NoDataAvailableTemplate';
import { WidgetHeader } from '../../common/WidgetHeader';
import { useWidgetPopout } from '~/hooks/useWidgetPopout';

// Helper function to format display values
const formatDisplayValue = (value: string | number): string => {
  if (typeof value === 'string') {
    return value;
  }
  return value.toString();
};

interface ProjectedMetric {
  label: string;
  currentValue: string | number;
  projectedValue: string | number;
  isPositiveChange: boolean;
}

export type ProjectedRiskMetricsWidgetProps = {
  data: ProjectedMetric[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  handleRemoveWidget?: (widgetId: string) => void;
  widgetId: string;
  title?: string;
  snapshotId?: string | null;
};

export const ProjectedRiskMetricsWidget: React.FC<
  ProjectedRiskMetricsWidgetProps
> = ({
  data,
  isLoading,
  error,
  refetch,
  handleRemoveWidget,
  widgetId,
  title = 'Projected Risk: Current vs Proposed',
  snapshotId,
}) => {
  const removeWidget = useCallback(
    () => handleRemoveWidget?.(widgetId),
    [widgetId, handleRemoveWidget],
  );

  // Add popout functionality
  const handlePopout = useWidgetPopout({
    widgetId,
    widgetComponent: ProjectedRiskMetricsWidget,
    title: title || 'Projected Risk: Current vs Proposed',
    defaultWidth: 800,
    defaultHeight: 600,
    widgetProps: {
      data,
      isLoading,
      error,
      refetch,
      handleRemoveWidget,
      widgetId,
      title,
      snapshotId,
    },
  });
  const setSelectWidgetAI = useSetAtom(selectWidgetAIAtom);
  const noData = useMemo(() => {
    return !data.length;
  }, [data]);

  if (isLoading) {
    return (
      <div className="p-3 h-full">
        <Skeleton className="h-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full hover-container" id={widgetId}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          refetch={() => refetch()}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          icon="pi pi-exclamation-triangle"
          title="Something went wrong."
          isError
        />
      </div>
    );
  }

  if (!snapshotId) {
    return (
      <div className="h-full hover-container" id={widgetId}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          icon="pi pi-exclamation-triangle"
          title="No Snapshot Available."
          subTitle="Run portfolio analysis to generate snapshot"
        />
      </div>
    );
  }

  if (noData) {
    return (
      <div className="h-full hover-container" id={widgetId}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          refetch={() => refetch()}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          title="No projected risk metrics data available."
          icon="pi pi-chart-line"
        />
      </div>
    );
  }

  return (
    <div className="hover-container h-full flex flex-column" id={widgetId}>
      <WidgetHeader
        title={title}
        removeWidget={removeWidget}
        refetch={() => refetch()}
        setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
        onPopout={handlePopout}
        widgetId={widgetId}
      />

      {/* Full Height Card with Scrollable Content */}
      <div className="flex-1 p-3 overflow-y-auto">
        <Card
          className="h-full flex flex-column"
          pt={{
            body: {
              className: 'p-3 flex-1',
            },
            content: {
              className: 'p-0 m-0 flex-1',
            },
          }}
        >
          {data.map((metric, index) => (
            <div
              key={`${metric.label}-${index}`}
              className="flex align-items-center justify-content-between border-round p-3"
              style={{
                transition: 'all 0.2s ease',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor =
                  'var(--bq-surface-elevated)';
                e.currentTarget.style.boxShadow =
                  '0 2px 4px rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <span style={{ color: 'var(--bq-text-secondary)' }}>
                {metric.label}
              </span>
              <div className="flex align-items-center gap-2">
                <span style={{ color: 'var(--bq-text-secondary)' }}>
                  {formatDisplayValue(metric.currentValue)}
                </span>
                <span style={{ color: 'var(--bq-text-tertiary)' }}>→</span>
                <span
                  className="font-semibold"
                  style={{ color: 'var(--bq-text-primary)' }}
                >
                  {formatDisplayValue(metric.projectedValue)}
                </span>
                <IndicatorDot
                  color={metric.isPositiveChange ? 'green' : 'red'}
                />
              </div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
};
